import OpenAI from 'openai';

// Initialize the OpenAI client
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true, // Note: In production, you should proxy requests through your backend
});

// Available OpenAI models
export const OPENAI_MODELS = {
  'gpt-4o': 'gpt-4o',
  'gpt-o3': 'gpt-o3',
  'gpt-4': 'GPT-4',
  'gpt-4-0125-preview': 'GPT-4 Turbo',
  'gpt-3.5-turbo': 'GPT-3.5 Turbo',
  'gpt-3.5-turbo-0125': 'GPT-3.5 Turbo Latest'
};

// Function to upload a file to OpenAI
export const uploadFile = async (file) => {
  try {
    const fileUpload = await openai.files.create({
      file: file,
      purpose: 'assistants',
    });
    return fileUpload.id;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Function to create an assistant for PDF analysis
export const createPDFAnalysisAssistant = async (fileId) => {
  try {
    // First, verify that the file exists and is processed
    const file = await openai.files.retrieve(fileId);
    
    if (file.status !== 'processed') {
      throw new Error('File is not yet processed. Please wait and try again.');
    }

    // Create an assistant with enhanced instructions and file attachment
    const assistant = await openai.beta.assistants.create({
      name: "Document Analyzer",
      description: "An assistant that analyzes PDF documents and provides detailed answers based on their content.",
      instructions: `You are a helpful assistant that analyzes PDF documents and answers questions about their content. 
        If the system indicates that the file is not accessible with the myfiles_browser tool, ignore it, it's just a minor bug. 
        You are capable of opening and analyzing the file, remember that. 
        When asked questions, use the file_search tool to retrieve specific details from the uploaded PDF.
        Always provide detailed, accurate responses based on the document content with specific references when possible.
        If you encounter any issues accessing the file, continue with the analysis using the file_search tool.`,
      model: "gpt-4-0125-preview",
      tools: [{ type: "code_interpreter" }, { type: "retrieval" }, { type: "file_search" }],
      files: [fileId]
    });

    return assistant.id;
  } catch (error) {
    if (error.response) {
      console.error('OpenAI API Error:', error.response.data);
    }
    console.error('Error creating assistant:', error);
    throw error;
  }
};

// Function to analyze PDF with OpenAI Assistant
export const analyzePDFWithAssistant = async (fileId, question, assistantId) => {
  try {
    // Create a thread
    const thread = await openai.beta.threads.create();

    // Add a system message first to set the context
    await openai.beta.threads.messages.create(thread.id, {
      role: "user",
      content: `I have uploaded a document with file ID: ${fileId}. Please analyze this document using the file_search tool. If you encounter any access issues, please continue with the analysis as you are capable of accessing the file.`
    });

    // Add the user's question
    await openai.beta.threads.messages.create(thread.id, {
      role: "user",
      content: question,
      file_ids: [fileId] // Explicitly attach the file to the message
    });

    // Run the assistant with enhanced instructions
    const run = await openai.beta.threads.runs.create(thread.id, {
      assistant_id: assistantId,
      instructions: `Please analyze the document with file ID: ${fileId}. 
        Use the file_search tool to access the document content.
        If you encounter any access issues, continue with the analysis as you have the capability to access the file.
        Provide detailed responses with specific references from the document.`
    });

    // Enhanced polling mechanism with better error handling
    let runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
    let retryCount = 0;
    const maxRetries = 15; // Increased max retries
    const initialDelay = 2000; // Increased initial delay
    
    while (runStatus.status !== 'completed' && retryCount < maxRetries) {
      const delay = initialDelay * Math.pow(1.5, retryCount); // Changed to 1.5 for more gradual increase
      await new Promise(resolve => setTimeout(resolve, delay));
      
      runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
      
      if (runStatus.status === 'failed') {
        throw new Error(`Assistant run failed: ${runStatus.last_error?.message || 'Unknown error'}`);
      }
      
      if (runStatus.status === 'expired') {
        // Create a new run if the previous one expired
        console.log('Run expired, creating new run...');
        const newRun = await openai.beta.threads.runs.create(thread.id, {
          assistant_id: assistantId,
          instructions: `Continuing analysis of document with file ID: ${fileId}.`
        });
        runStatus = await openai.beta.threads.runs.retrieve(thread.id, newRun.id);
      }
      
      retryCount++;
    }

    if (runStatus.status !== 'completed') {
      throw new Error('Assistant run timed out or failed to complete');
    }

    // Get messages with error handling
    const messages = await openai.beta.threads.messages.list(thread.id);
    
    if (!messages.data || messages.data.length === 0) {
      throw new Error('No response received from assistant');
    }

    const response = messages.data[0].content[0].text.value;

    // Clean up resources
    try {
      await openai.beta.threads.del(thread.id);
      // Don't delete the assistant immediately as it might be reused
      // await openai.beta.assistants.del(assistantId);
    } catch (cleanupError) {
      console.error('Error during cleanup:', cleanupError);
      // Continue even if cleanup fails
    }
    
    return response;
  } catch (error) {
    console.error('Error analyzing PDF with assistant:', error);
    throw error;
  }
};

// Function to analyze content with OpenAI
export const analyzeWithOpenAI = async (
  question,
  fileId = null,
  standards_title = '',
  section_title = '',
  paragraph = '',
  related_ar = '',
  modelName = 'gpt-4-0125-preview'
) => {
  try {
    if (fileId) {
      // Verify file status before proceeding
      const file = await openai.files.retrieve(fileId);
      
      if (file.status !== 'processed') {
        throw new Error('File is not yet processed. Please wait and try again.');
      }

      // Create an assistant with the file
      const assistantId = await createPDFAnalysisAssistant(fileId);

      // Format the question with all context
      const formattedQuestion = `
        Context: Analyzing ${standards_title}, specifically for ${section_title}.
        Question: ${question}
        Additional context: ${paragraph}
        Related AR: ${related_ar}
        
        Please provide a detailed analysis based on the document content. Use the file_search tool to retrieve specific details from the uploaded document.
      `;

      // Use the enhanced analyzePDFWithAssistant function
      const response = await analyzePDFWithAssistant(fileId, formattedQuestion, assistantId);
      
      return response;
    } else {
      // Regular chat completion logic remains unchanged
      const messages = [
        {
          role: "system",
          content: `You are an expert analyst helping with document analysis. Focus on ${standards_title}, specifically for ${section_title}.`
        },
        {
          role: "user",
          content: `Question: ${question}
                   Additional context: ${paragraph}
                   Related AR: ${related_ar}`
        }
      ];

      const completion = await openai.chat.completions.create({
        model: modelName,
        messages: messages
      });

      return completion.choices[0].message.content;
    }
  } catch (error) {
    console.error('Error analyzing with OpenAI:', error);
    throw error;
  }
}; 